import { auth, db } from '../../src/firebase.js';
import {
  collection, query, orderBy, onSnapshot, doc, getDoc, getDocs,
  updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { sidebarPage } from '../utils/sidebarPageLayout.js';
import {
  PLANS, PLAN_LIMITS, TIAGO_FREE_LIMIT, LEAGUE_OPTIONS, MODE_OPTIONS, getQuotaState,
} from '../tiago/tiagoConfig.js';
import { startTiagoCheckout, openTiagoBillingPortal } from '../tiago/tiagoStripe.js';
import { renderTiagoMarkdown, messagesToMarkdown } from '../tiago/tiagoMarkdown.js';

export class TiagoAI {
  constructor(containerId) {
    this.containerId = containerId;
    this.currentConversationId = `chat_${Date.now()}`;
    this.conversations = [];
    this.messages = [];
    this.userData = { tiagoFreeUsed: 0, messageCount: 0 };
    this.isTyping = false;
    this.sidebarOpen = true;
    this.searchQuery = '';
    this.isDarkMode = localStorage.getItem('tiago-dark') === '1';
    this.context = { league: '', mode: 'fan' };
    this.unsubMessages = null;
    this.unsubHistory = null;
    this.showSettings = false;
    this.paywallFocusFree = true;
    this.toast = null;
  }

  destroy() {
    if (this.unsubMessages) this.unsubMessages();
    if (this.unsubHistory) this.unsubHistory();
    this.unsubMessages = null;
    this.unsubHistory = null;
  }

  async init() {
    const el = document.getElementById(this.containerId);
    if (!el || !auth.currentUser) return;

    this.injectStyles();
    await this.fetchUserData();
    this.checkReturnFromStripe();
    this.render();
    this.setupHistoryListener();
    this.setupMessagesListener();
  }

  checkReturnFromStripe() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('tiago') === 'success') {
      this.toast = 'Welcome! Your Tiago plan is active.';
      window.history.replaceState({}, '', `${window.location.pathname}?page=tiago-ai`);
    }
  }

  async fetchUserData() {
    const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
    if (userDoc.exists()) {
      this.userData = { ...this.userData, ...userDoc.data() };
    }
  }

  setupHistoryListener() {
    if (this.unsubHistory) this.unsubHistory();
    const q = query(
      collection(db, 'users', auth.currentUser.uid, 'conversations'),
      orderBy('updatedAt', 'desc'),
    );
    this.unsubHistory = onSnapshot(q, (snap) => {
      this.conversations = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      this.updateSidebar();
    });
  }

  setupMessagesListener() {
    if (this.unsubMessages) this.unsubMessages();
    const messagesRef = collection(
      db, 'users', auth.currentUser.uid, 'conversations', this.currentConversationId, 'messages',
    );
    const q = query(messagesRef, orderBy('createdAt', 'asc'));
    this.unsubMessages = onSnapshot(q, (snap) => {
      this.messages = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      this.renderMessages();
    });
  }

  updateSidebar() {
    const list = document.getElementById('chat-history-list');
    if (!list) return;
    const filtered = this.conversations.filter((c) =>
      (c.title || 'New Chat').toLowerCase().includes(this.searchQuery.toLowerCase()),
    );
    list.innerHTML = filtered.length ? filtered.map((c) => `
      <div class="chat-history-item ${c.id === this.currentConversationId ? 'active' : ''}" data-id="${c.id}">
        <span class="chat-title">${this._esc(c.title || 'New Chat')}</span>
        <button type="button" class="chat-delete-btn" data-delete="${c.id}" title="Delete">×</button>
      </div>
    `).join('') : '<p class="tiago-empty-history">No chats yet — ask Tiago something!</p>';

    list.querySelectorAll('.chat-history-item').forEach((item) => {
      item.addEventListener('click', (e) => {
        if (e.target.closest('.chat-delete-btn')) return;
        this.loadConversation(item.dataset.id);
      });
    });
    list.querySelectorAll('.chat-delete-btn').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        if (!confirm('Delete this chat?')) return;
        await this.deleteConversation(btn.dataset.delete);
      });
    });
  }

  async loadConversation(id) {
    this.currentConversationId = id;
    this.setupMessagesListener();
    this.updateSidebar();
    const welcome = document.querySelector('.chat-welcome');
    if (welcome) welcome.remove();
  }

  async deleteConversation(id) {
    try {
      const fn = httpsCallable(getFunctions(), 'deleteTiagoConversation');
      await fn({ conversationId: id });
      if (id === this.currentConversationId) this.startNewChat(false);
    } catch (err) {
      console.error(err);
      alert('Could not delete chat.');
    }
  }

  startNewChat(reRender = true) {
    this.currentConversationId = `chat_${Date.now()}`;
    this.messages = [];
    if (reRender) {
      this.render();
      this.setupHistoryListener();
    }
    this.setupMessagesListener();
  }

  render() {
    const el = document.getElementById(this.containerId);
    const quota = getQuotaState(this.userData);
    const plan = quota.planMeta;

    const main = `
      <div class="tiago-container ${this.sidebarOpen ? 'sidebar-open' : ''} ${this.isDarkMode ? 'dark-mode' : ''}">
        ${this.toast ? `<div class="tiago-toast">${this._esc(this.toast)}</div>` : ''}
        <header class="tiago-header">
          <img src="/images/tiago.png" class="tiago-trigger" id="sidebar-toggle" alt="Tiago" title="Toggle sidebar">
          <div class="tiago-header-actions">
            <button type="button" id="tiago-settings-btn" class="tiago-icon-btn" title="Settings">⚙</button>
          </div>
        </header>

        <aside class="tiago-sidebar">
          <div class="sidebar-header">
            <button type="button" id="new-chat-btn" class="tiago-new-btn">+ New chat</button>
            <div class="sidebar-search">
              <input type="text" id="history-search" placeholder="Search chats…" value="${this._esc(this.searchQuery)}">
            </div>
          </div>
          <div id="chat-history-list" class="chat-history-list"></div>
          <div class="sidebar-footer">
            <div class="tiago-quota-box">
              <div class="quota-header">
                ${quota.isFree
    ? '<span class="quota-plan-badge" style="background:#888">Free</span>'
    : `<span class="quota-plan-badge" style="background:${plan?.color || '#888'}">${plan?.name || ''}</span>`}
                <span class="quota-text">${quota.label}</span>
              </div>
              ${quota.limit !== -1 ? `<div class="quota-bar"><div class="quota-fill" style="width:${quota.pct}%"></div></div>` : ''}
              <button type="button" class="quota-upgrade-btn" id="show-plans-btn">
                ${quota.isFree ? 'Upgrade from €3.99/mo' : 'Manage plan'}
              </button>
            </div>
            <button type="button" id="theme-toggle" class="theme-toggle-btn">
              ${this.isDarkMode ? '☀️ Light' : '🌙 Dark'}
            </button>
          </div>
        </aside>

        <main class="tiago-main">
          <div class="tiago-context-bar">
            <select id="tiago-league" class="tiago-select" title="League focus">
              ${LEAGUE_OPTIONS.map((o) =>
    `<option value="${o.id}" ${this.context.league === o.id ? 'selected' : ''}>${o.label}</option>`,
  ).join('')}
            </select>
            <select id="tiago-mode" class="tiago-select" title="Response style">
              ${MODE_OPTIONS.map((o) =>
    `<option value="${o.id}" ${this.context.mode === o.id ? 'selected' : ''}>${o.label}</option>`,
  ).join('')}
            </select>
            <button type="button" id="tiago-export-btn" class="tiago-toolbar-btn" title="Export chat">Export</button>
            <button type="button" id="tiago-share-btn" class="tiago-toolbar-btn" title="Share read-only link">Share</button>
          </div>

          ${this.showSettings ? this._settingsPanel() : ''}

          <div id="chat-window" class="chat-window">
            ${this.messages.length === 0 ? this._welcomeHTML() : ''}
          </div>
          <div class="chat-input-area">
            <div class="input-wrapper">
              <textarea id="chat-input" rows="1" placeholder="Ask Tiago about women's football…" autocomplete="off"></textarea>
              <button type="button" id="send-btn" class="send-btn" title="Send">➤</button>
            </div>
            <p class="tiago-disclaimer">Tiago specialises in women's football. ${quota.isFree ? `${TIAGO_FREE_LIMIT - quota.used} free questions left.` : ''} Answers may be imperfect — verify important facts.</p>
          </div>
        </main>
      </div>
      ${this._upgradeModalHTML()}
    `;

    el.innerHTML = sidebarPage({ title: '', main, wide: true, pageClass: 'tiago-page' });
    this.attachListeners();
    this.renderMessages();
    this.updateSidebar();
    if (this.toast) setTimeout(() => { this.toast = null; }, 5000);
  }

  _welcomeHTML() {
    return `
      <div class="chat-welcome">
        <img src="/images/tiago.png" class="welcome-avatar" alt="">
        <h1>How can Tiago help you today?</h1>
        <p class="welcome-intro">Your women's football expert — tactics, players, leagues, coaching. ${!this.userData.plan ? `You have <strong>${TIAGO_FREE_LIMIT} free questions</strong> to try Tiago.` : ''}</p>
        <div class="tiago-suggestions">
          <button type="button" class="tiago-suggestion-chip" data-query="What formations work best in women's football?">⚽ Best formations in the women's game?</button>
          <button type="button" class="tiago-suggestion-chip" data-query="Who are the best young players in the WSL right now?">🏆 Best young WSL players?</button>
          <button type="button" class="tiago-suggestion-chip" data-query="How do I coach a high press effectively?">🧠 Coaching a high press</button>
        </div>
      </div>
    `;
  }

  _settingsPanel() {
    const instr = this._esc(this.userData.tiagoCustomInstructions || '');
    return `
      <div class="tiago-settings-panel">
        <label for="tiago-custom-instr">Custom instructions <span class="tiago-hint">(tone, role, topics — max ~800 chars)</span></label>
        <textarea id="tiago-custom-instr" rows="3" maxlength="800" placeholder="e.g. I'm a U16 coach in Spain…">${instr}</textarea>
        <button type="button" id="tiago-save-instr" class="tiago-toolbar-btn">Save preferences</button>
      </div>
    `;
  }

  _pricingCardsHTML(context = 'modal', focusPlanId = null) {
    return PLANS.map((p) => {
      const isFocus = focusPlanId === p.id;
      return `
      <div class="tiago-plan-card ${p.highlight ? 'highlighted' : ''} ${isFocus ? 'tiago-plan-focus' : ''}">
        ${p.highlight ? '<div class="plan-popular-badge">Most Popular</div>' : ''}
        ${isFocus ? '<div class="plan-popular-badge">Start here</div>' : ''}
        <div class="plan-name" style="color:${p.color}">${p.name}</div>
        <div class="plan-price">${p.price}<span class="plan-period">/${p.period}</span></div>
        <div class="plan-messages">${p.label}</div>
        <ul class="plan-features">${p.features.map((f) => `<li>✓ ${f}</li>`).join('')}</ul>
        <button type="button" class="plan-cta-btn ${p.highlight || isFocus ? 'plan-cta-primary' : ''}"
          data-plan="${p.id}"
          style="${p.highlight || isFocus ? `background:${p.color};color:#fff;border-color:${p.color}` : `border-color:${p.color};color:${p.color}`}">
          Subscribe — ${p.price}/${p.period}
        </button>
      </div>
    `;
    }).join('');
  }

  _upgradeModalHTML() {
    const fan = PLANS[0];
    return `
      <div id="upgrade-modal" class="fg-modal-overlay">
        <div class="fg-modal tiago-upgrade-modal">
          <button type="button" class="tiago-modal-close" id="tiago-modal-close">✕</button>
          <img src="/images/tiago.png" class="tiago-modal-avatar" alt="">
          <h2>You've used your ${TIAGO_FREE_LIMIT} free questions</h2>
          <p>Keep chatting with Tiago — subscribe from <strong>${fan.price}/${fan.period}</strong> (Fan plan) or pick a higher tier.</p>
          <div class="tiago-paywall-hero">
            <div class="tiago-plan-card tiago-plan-focus highlighted">
              <div class="plan-name" style="color:${fan.color}">${fan.name}</div>
              <div class="plan-price">${fan.price}<span class="plan-period">/${fan.period}</span></div>
              <div class="plan-messages">${fan.label}</div>
              <button type="button" class="plan-cta-btn plan-cta-primary" data-plan="fan"
                style="background:${fan.color};color:#fff">
                Continue with Fan — ${fan.price}/${fan.period}
              </button>
            </div>
          </div>
          <p class="tiago-modal-or">Or compare all plans</p>
          <div class="tiago-plans-grid compact">${this._pricingCardsHTML('modal')}</div>
        </div>
      </div>
    `;
  }

  attachListeners() {
    const input = document.getElementById('chat-input');
    const sendBtn = document.getElementById('send-btn');

    const handleSend = (text) => {
      const msg = (text || input?.value || '').trim();
      if (msg) this.sendMessage(msg);
    };

    sendBtn?.addEventListener('click', () => handleSend());
    input?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    });

    document.getElementById('new-chat-btn')?.addEventListener('click', () => this.startNewChat());
    document.getElementById('sidebar-toggle')?.addEventListener('click', () => {
      this.sidebarOpen = !this.sidebarOpen;
      document.querySelector('.tiago-container')?.classList.toggle('sidebar-open');
    });
    document.getElementById('history-search')?.addEventListener('input', (e) => {
      this.searchQuery = e.target.value;
      this.updateSidebar();
    });
    document.getElementById('theme-toggle')?.addEventListener('click', () => {
      this.isDarkMode = !this.isDarkMode;
      localStorage.setItem('tiago-dark', this.isDarkMode ? '1' : '0');
      this.render();
      this.setupHistoryListener();
      this.setupMessagesListener();
    });
    document.getElementById('show-plans-btn')?.addEventListener('click', () => {
      const q = getQuotaState(this.userData);
      if (q.isFree) {
        document.getElementById('upgrade-modal')?.classList.add('is-open');
      } else {
        openTiagoBillingPortal().catch((err) => alert(err.message));
      }
    });
    document.getElementById('tiago-modal-close')?.addEventListener('click', () => {
      document.getElementById('upgrade-modal')?.classList.remove('is-open');
    });
    document.getElementById('upgrade-modal')?.addEventListener('click', (e) => {
      if (e.target.id === 'upgrade-modal') e.target.classList.remove('is-open');
    });

    document.querySelectorAll('.plan-cta-btn').forEach((btn) => {
      btn.addEventListener('click', () => this._onPlanClick(btn));
    });

    document.querySelectorAll('.tiago-suggestion-chip').forEach((chip) => {
      chip.addEventListener('click', () => handleSend(chip.dataset.query));
    });

    document.getElementById('tiago-league')?.addEventListener('change', (e) => {
      this.context.league = e.target.value;
    });
    document.getElementById('tiago-mode')?.addEventListener('change', (e) => {
      this.context.mode = e.target.value;
    });
    document.getElementById('tiago-settings-btn')?.addEventListener('click', () => {
      this.showSettings = !this.showSettings;
      this.render();
      this.setupHistoryListener();
      this.setupMessagesListener();
    });
    document.getElementById('tiago-save-instr')?.addEventListener('click', () => this.saveCustomInstructions());
    document.getElementById('tiago-export-btn')?.addEventListener('click', () => this.exportChat());
    document.getElementById('tiago-share-btn')?.addEventListener('click', () => this.shareChat());
  }

  async _onPlanClick(btn) {
    const planId = btn.dataset.plan;
    if (!planId) return;
    btn.disabled = true;
    const prev = btn.textContent;
    btn.textContent = 'Redirecting…';
    try {
      await startTiagoCheckout(planId);
    } catch (err) {
      btn.disabled = false;
      btn.textContent = prev;
      alert(err.message || 'Could not start checkout. Add Stripe price IDs in Firebase secrets and VITE_ env vars.');
    }
  }

  async saveCustomInstructions() {
    const ta = document.getElementById('tiago-custom-instr');
    const val = (ta?.value || '').trim().slice(0, 800);
    await updateDoc(doc(db, 'users', auth.currentUser.uid), {
      tiagoCustomInstructions: val,
      updatedAt: serverTimestamp(),
    });
    this.userData.tiagoCustomInstructions = val;
    const btn = document.getElementById('tiago-save-instr');
    if (btn) {
      btn.textContent = 'Saved ✓';
      setTimeout(() => { btn.textContent = 'Save preferences'; }, 2000);
    }
  }

  exportChat() {
    if (!this.messages.length) {
      alert('Nothing to export yet.');
      return;
    }
    const title = this.conversations.find((c) => c.id === this.currentConversationId)?.title || 'Tiago chat';
    const md = messagesToMarkdown(this.messages, title);
    const blob = new Blob([md], { type: 'text/markdown' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `tiago-${Date.now()}.md`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  async shareChat() {
    if (!this.messages.length) {
      alert('Send at least one message before sharing.');
      return;
    }
    try {
      const fn = httpsCallable(getFunctions(), 'createTiagoShareLink');
      const res = await fn({
        conversationId: this.currentConversationId,
        origin: window.location.origin,
      });
      const url = res.data?.url;
      if (url) {
        await navigator.clipboard.writeText(url);
        alert('Share link copied to clipboard.');
      }
    } catch (err) {
      console.error(err);
      alert('Could not create share link.');
    }
  }

  async sendMessage(text) {
    if (this.isTyping) return;

    const quota = getQuotaState(this.userData);
    if (quota.exhausted) {
      document.getElementById('upgrade-modal')?.classList.add('is-open');
      return;
    }

    document.querySelector('.chat-welcome')?.remove();

    const input = document.getElementById('chat-input');
    if (input) input.value = '';

    this.isTyping = true;
    this.addTypingIndicator();

    try {
      const chatFn = httpsCallable(getFunctions(), 'chat');
      const ctx = {};
      if (this.context.league) ctx.league = this.context.league;
      if (this.context.mode) ctx.mode = this.context.mode;

      const result = await chatFn({
        message: text,
        conversationId: this.currentConversationId,
        context: ctx,
      });

      this.removeTypingIndicator();
      const data = result.data;
      if (data.isFreeTier) {
        this.userData.tiagoFreeUsed = data.currentCount;
      } else {
        this.userData.messageCount = data.currentCount;
        if (data.plan) this.userData.plan = data.plan;
      }
      this._updateQuotaDisplay();
      if (getQuotaState(this.userData).exhausted) {
        setTimeout(() => document.getElementById('upgrade-modal')?.classList.add('is-open'), 400);
      }
    } catch (error) {
      this.removeTypingIndicator();
      const code = error.details?.code || error.code || '';
      if (code === 'quota-exceeded' || code === 'failed-precondition') {
        document.getElementById('upgrade-modal')?.classList.add('is-open');
      } else {
        console.error('Chat Error:', error);
        alert('Sorry, Tiago could not respond. Check your connection or try again.');
      }
    } finally {
      this.isTyping = false;
    }
  }

  async regenerateLast() {
    const lastUser = [...this.messages].reverse().find((m) => m.sender === 'user');
    if (!lastUser || this.isTyping) return;
    this.isTyping = true;
    this.addTypingIndicator();
    try {
      const chatFn = httpsCallable(getFunctions(), 'chat');
      const ctx = {};
      if (this.context.league) ctx.league = this.context.league;
      if (this.context.mode) ctx.mode = this.context.mode;
      const result = await chatFn({
        message: lastUser.text,
        conversationId: this.currentConversationId,
        context: ctx,
        regenerate: true,
      });
      this.removeTypingIndicator();
      const data = result.data;
      if (data.isFreeTier) this.userData.tiagoFreeUsed = data.currentCount;
      else this.userData.messageCount = data.currentCount;
      this._updateQuotaDisplay();
    } catch (error) {
      this.removeTypingIndicator();
      console.error(error);
      alert('Could not regenerate.');
    } finally {
      this.isTyping = false;
    }
  }

  _updateQuotaDisplay() {
    const quota = getQuotaState(this.userData);
    const quotaText = document.querySelector('.quota-text');
    const quotaFill = document.querySelector('.quota-fill');
    if (quotaText) quotaText.textContent = quota.label;
    if (quotaFill && quota.limit !== -1) quotaFill.style.width = `${quota.pct}%`;
  }

  renderMessages() {
    const windowEl = document.getElementById('chat-window');
    if (!windowEl) return;
    if (this.messages.length === 0) {
      if (!windowEl.querySelector('.chat-welcome')) {
        windowEl.innerHTML = this._welcomeHTML();
      }
      return;
    }
    windowEl.innerHTML = this.messages.map((m, i) => {
      const isLastAssistant = m.sender === 'assistant' && i === this.messages.length - 1;
      const body = m.sender === 'assistant'
        ? `<div class="msg-bubble tiago-md">${renderTiagoMarkdown(m.text)}</div>`
        : `<div class="msg-bubble">${this._esc(m.text)}</div>`;
      return `
      <div class="message-row ${m.sender}" data-idx="${i}">
        ${m.sender === 'assistant' ? '<img src="/images/tiago.png" class="msg-avatar" alt="">' : ''}
        <div class="msg-content-wrap">
          ${body}
          <div class="msg-actions">
            ${m.sender === 'assistant' ? `
              <button type="button" class="msg-action-btn" data-copy="${i}">Copy</button>
              ${isLastAssistant ? '<button type="button" class="msg-action-btn" data-regen>Regenerate</button>' : ''}
            ` : ''}
          </div>
        </div>
      </div>`;
    }).join('');
    windowEl.scrollTop = windowEl.scrollHeight;

    windowEl.querySelectorAll('[data-copy]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = Number(btn.dataset.copy);
        const t = this.messages[idx]?.text;
        if (t) navigator.clipboard.writeText(t);
        btn.textContent = 'Copied';
        setTimeout(() => { btn.textContent = 'Copy'; }, 1500);
      });
    });
    windowEl.querySelector('[data-regen]')?.addEventListener('click', () => this.regenerateLast());
  }

  addTypingIndicator() {
    const windowEl = document.getElementById('chat-window');
    if (!windowEl) return;
    const div = document.createElement('div');
    div.id = 'typing-indicator';
    div.className = 'message-row assistant';
    div.innerHTML = '<img src="/images/tiago.png" class="msg-avatar" alt=""><div class="msg-bubble typing"><span></span><span></span><span></span></div>';
    windowEl.appendChild(div);
    windowEl.scrollTop = windowEl.scrollHeight;
  }

  removeTypingIndicator() {
    document.getElementById('typing-indicator')?.remove();
  }

  _esc(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  injectStyles() {
    if (document.getElementById('tiago-styles')) return;
    const style = document.createElement('style');
    style.id = 'tiago-styles';
    style.textContent = `
      .tiago-container { display: flex; height: calc(100vh - 60px); margin: -24px; background: #fff; color: #1f1f1f; position: relative; }
      .tiago-container.dark-mode { background: #131314; color: #e3e3e3; }
      .tiago-toast { position: fixed; top: 72px; left: 50%; transform: translateX(-50%); background: #1a1a2e; color: #fff; padding: 12px 24px; border-radius: 8px; z-index: 2000; font-size: 0.9rem; }
      .tiago-header { position: absolute; top: 16px; left: 16px; right: 16px; z-index: 100; display: flex; align-items: center; justify-content: space-between; }
      .tiago-trigger { width: 40px; height: 40px; border-radius: 50%; cursor: pointer; border: 2px solid transparent; background: #fff; }
      .tiago-trigger:hover { border-color: #e91e8c; }
      .tiago-icon-btn { background: #f0f2f5; border: none; border-radius: 8px; padding: 8px 12px; cursor: pointer; font-size: 1rem; }
      .dark-mode .tiago-icon-btn { background: #28292a; color: #e3e3e3; }
      .tiago-sidebar { width: 0; background: #f8f9fa; display: flex; flex-direction: column; overflow: hidden; transition: width 0.3s; border-right: 1px solid #e0e0e0; }
      .sidebar-open .tiago-sidebar { width: 280px; padding: 16px; }
      .dark-mode .tiago-sidebar { background: #1e1f20; border-right-color: #333; }
      .sidebar-header { margin-top: 50px; }
      .tiago-new-btn { width: 100%; background: #e8eaed; border: none; color: #1f1f1f; padding: 12px 20px; border-radius: 24px; text-align: left; font-weight: 500; cursor: pointer; margin-bottom: 16px; }
      .dark-mode .tiago-new-btn { background: #28292a; color: #e3e3e3; }
      .sidebar-search input { width: 100%; padding: 8px 16px; border-radius: 8px; border: 1px solid #ddd; box-sizing: border-box; }
      .chat-history-list { flex: 1; overflow-y: auto; }
      .chat-history-item { display: flex; align-items: center; justify-content: space-between; padding: 10px 12px; border-radius: 8px; cursor: pointer; font-size: 0.85rem; }
      .chat-history-item:hover { background: #e8eaed; }
      .chat-history-item.active { background: #fce4f3; color: #c2185b; font-weight: 600; }
      .chat-title { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .chat-delete-btn { background: none; border: none; color: #999; font-size: 1.1rem; cursor: pointer; padding: 0 4px; }
      .chat-delete-btn:hover { color: #c2185b; }
      .tiago-empty-history { font-size: 0.8rem; color: #888; padding: 8px; }
      .sidebar-footer { border-top: 1px solid #ddd; padding-top: 16px; }
      .tiago-quota-box { padding: 12px; background: #f0f2f5; border-radius: 12px; margin-bottom: 8px; }
      .quota-header { display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 0.78rem; }
      .quota-plan-badge { color: #fff; padding: 2px 8px; border-radius: 10px; font-weight: 700; font-size: 0.7rem; }
      .quota-bar { height: 4px; background: #ddd; border-radius: 2px; margin-bottom: 10px; }
      .quota-fill { height: 100%; background: #e91e8c; border-radius: 2px; transition: width 0.4s; }
      .quota-upgrade-btn { width: 100%; border: 1px solid #e91e8c; color: #e91e8c; background: none; border-radius: 8px; padding: 8px; cursor: pointer; font-size: 0.78rem; }
      .theme-toggle-btn { width: 100%; border: 1px solid #ddd; padding: 8px; border-radius: 8px; cursor: pointer; background: none; color: inherit; font-size: 0.8rem; }
      .tiago-main { flex: 1; display: flex; flex-direction: column; padding: 0 4% 0 8%; overflow: hidden; }
      .tiago-context-bar { display: flex; flex-wrap: wrap; gap: 8px; padding: 56px 0 12px; align-items: center; }
      .tiago-select { padding: 6px 10px; border-radius: 8px; border: 1px solid #ddd; font-size: 0.85rem; background: #fff; }
      .dark-mode .tiago-select { background: #28292a; border-color: #444; color: #e3e3e3; }
      .tiago-toolbar-btn { padding: 6px 12px; border-radius: 8px; border: 1px solid #ddd; background: #f8f9fa; cursor: pointer; font-size: 0.8rem; }
      .tiago-settings-panel { background: #f8f9fa; border-radius: 12px; padding: 16px; margin-bottom: 12px; }
      .tiago-settings-panel textarea { width: 100%; margin: 8px 0; padding: 10px; border-radius: 8px; border: 1px solid #ddd; box-sizing: border-box; font-family: inherit; }
      .tiago-hint { font-size: 0.75rem; color: #888; font-weight: 400; }
      .chat-window { flex: 1; overflow-y: auto; padding: 20px 0; display: flex; flex-direction: column; gap: 24px; }
      .chat-welcome { text-align: center; margin: auto; max-width: 640px; }
      .welcome-avatar { width: 64px; height: 64px; border-radius: 50%; margin-bottom: 16px; }
      .chat-welcome h1 { font-size: 2rem; background: linear-gradient(90deg, #e91e8c, #4285f4); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
      .tiago-suggestion-chip { display: block; width: 100%; max-width: 420px; margin: 8px auto; padding: 12px 16px; border-radius: 12px; border: 1px solid #ddd; background: #f0f2f5; cursor: pointer; text-align: left; }
      .message-row { display: flex; gap: 12px; max-width: 800px; width: 100%; margin: 0 auto; }
      .message-row.user { flex-direction: row-reverse; }
      .msg-avatar { width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0; }
      .msg-content-wrap { max-width: 85%; }
      .user .msg-bubble { background: #f0f2f5; padding: 12px 18px; border-radius: 18px; }
      .tiago-md { line-height: 1.6; }
      .tiago-md pre.tiago-code { background: #1e1f20; color: #e3e3e3; padding: 12px; border-radius: 8px; overflow-x: auto; font-size: 0.85rem; }
      .tiago-inline-code { background: #f0f2f5; padding: 2px 6px; border-radius: 4px; font-size: 0.9em; }
      .msg-actions { display: flex; gap: 8px; margin-top: 6px; }
      .msg-action-btn { background: none; border: none; color: #888; font-size: 0.75rem; cursor: pointer; text-decoration: underline; }
      .typing span { display: inline-block; width: 8px; height: 8px; background: #e91e8c; border-radius: 50%; margin: 0 2px; animation: tiago-bounce 1.2s infinite; }
      .typing span:nth-child(2) { animation-delay: 0.2s; }
      .typing span:nth-child(3) { animation-delay: 0.4s; }
      @keyframes tiago-bounce { 0%,60%,100%{transform:translateY(0)} 30%{transform:translateY(-6px)} }
      .chat-input-area { padding-bottom: 32px; max-width: 800px; width: 100%; margin: 0 auto; }
      .input-wrapper { display: flex; align-items: flex-end; gap: 8px; background: #f0f2f5; border-radius: 20px; padding: 8px 16px; border: 1px solid #ddd; }
      .input-wrapper textarea { flex: 1; border: none; background: none; resize: none; max-height: 120px; font-size: 1rem; outline: none; color: inherit; font-family: inherit; line-height: 1.4; padding: 8px 0; }
      .send-btn { background: none; border: none; font-size: 1.2rem; color: #e91e8c; cursor: pointer; padding: 8px; }
      .tiago-disclaimer { font-size: 0.72rem; color: #9aa0a6; text-align: center; margin-top: 10px; }
      .fg-modal-overlay { display: none; position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 1000; align-items: center; justify-content: center; overflow-y: auto; padding: 24px; }
      .fg-modal-overlay.is-open { display: flex; }
      .tiago-upgrade-modal { background: #fff; border-radius: 20px; padding: 32px 24px; max-width: 960px; width: 100%; text-align: center; position: relative; max-height: 90vh; overflow-y: auto; }
      .tiago-paywall-hero { max-width: 320px; margin: 0 auto 24px; }
      .tiago-modal-or { color: #888; font-size: 0.85rem; margin: 16px 0; }
      .tiago-plans-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
      .tiago-plans-grid.compact .tiago-plan-card { padding: 16px 12px; }
      .tiago-plan-card { border: 1.5px solid #e0e0e0; border-radius: 16px; padding: 24px 16px; position: relative; text-align: left; }
      .tiago-plan-focus { border-color: #e91e8c; box-shadow: 0 4px 20px rgba(233,30,140,0.15); }
      .plan-popular-badge { position: absolute; top: -10px; left: 50%; transform: translateX(-50%); background: #e91e8c; color: #fff; font-size: 0.7rem; padding: 4px 12px; border-radius: 12px; white-space: nowrap; }
      .plan-cta-btn { width: 100%; margin-top: 12px; padding: 10px; border-radius: 10px; font-weight: 600; cursor: pointer; border: 1.5px solid #ddd; background: transparent; }
      .plan-cta-primary { color: #fff !important; }
      .tiago-share-page { max-width: 800px; margin: 0 auto; padding: 24px; }
      .tiago-share-note { color: #888; font-size: 0.85rem; margin-bottom: 24px; }
      @media (max-width: 900px) { .tiago-plans-grid { grid-template-columns: repeat(2, 1fr); } .tiago-main { padding: 0 16px; } }
      @media (max-width: 560px) { .tiago-plans-grid { grid-template-columns: 1fr; } .sidebar-open .tiago-sidebar { position: fixed; z-index: 200; height: calc(100vh - 60px); top: 60px; } }
    `;
    document.head.appendChild(style);
  }
}
